import { ClickAwayListener } from "@mui/material";
import { useMountEffect } from "@react-hookz/web";
import type { FunctionComponent } from "react";
import { useCallback, useRef, useState } from "react";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";

import GoogleIcon from "./icon-google.png";
import {
  Container,
  GoogleNewsLink,
  ImageGoogle,
  InfoIconButton,
  StyledInfoIcon,
  StyledTooltip,
  TextContent,
  ToolTipMessage,
  TooltipText,
} from "./styles";

const GOOGLE_NEWS_URL = "https://www.google.com/preferences/source?q=scmp.com";
const TOOLTIP_ID = "google-news-sources-tooltip";
const TOOLTIP_TEXT =
  "See more of the news you trust. Make SCMP a preferred source to prioritise our updates in your Google search results.";

interface Props {
  articleId?: string;
  className?: string;
}

export const ArticleGoogleNewsButton: FunctionComponent<Props> = ({ articleId, className }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const containerReference = useRef<HTMLDivElement>(null);

  const isEnglishLanguage = () => {
    const language =
      (window.navigator as Navigator & { userLanguage?: string }).userLanguage ??
      window.navigator.language;
    return language.toLowerCase().startsWith("en");
  };

  const handleClick = useCallback(() => {
    if (articleId) {
      sendGA4Tracking({
        action: "click",
        category: "google",
        customized_parameters: {
          article_id: articleId,
          page_type: "article",
        },
        subcategory: "preferred_source",
      });
    }
  }, [articleId]);

  const handleInfoClick = useCallback(() => {
    setIsTooltipOpen(previous => !previous);
  }, []);

  const handleTooltipClose = useCallback(() => {
    setIsTooltipOpen(false);
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsTooltipOpen(false);
    }
  }, []);

  useMountEffect(() => {
    setIsVisible(isEnglishLanguage());
  });

  if (!isVisible) {
    return null;
  }

  return (
    <Container className={className} ref={containerReference}>
      <GoogleNewsLink
        aria-label="Follow SCMP on Google News"
        href={GOOGLE_NEWS_URL}
        onClick={handleClick}
        rel="noopener noreferrer"
        target="_blank"
      >
        <ImageGoogle alt="Google News Preferred Icon" src={GoogleIcon.src} />
        <TextContent>Make SCMP preferred on Google</TextContent>
      </GoogleNewsLink>
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <StyledTooltip
          arrow
          open={isTooltipOpen}
          placement="bottom"
          PopperProps={{
            anchorEl: () => containerReference.current as HTMLElement,
            disablePortal: true,
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -4],
                },
              },
            ],
          }}
          title={
            <ToolTipMessage id={TOOLTIP_ID}>
              <TooltipText>{TOOLTIP_TEXT}</TooltipText>
            </ToolTipMessage>
          }
        >
          <InfoIconButton
            aria-describedby={isTooltipOpen ? TOOLTIP_ID : undefined}
            aria-expanded={isTooltipOpen}
            aria-label="More information"
            onClick={handleInfoClick}
            onKeyDown={handleKeyDown}
            type="button"
          >
            <StyledInfoIcon />
          </InfoIconButton>
        </StyledTooltip>
      </ClickAwayListener>
    </Container>
  );
};

ArticleGoogleNewsButton.displayName = "ArticleGoogleNewsButton";
